import React, { useState, useEffect } from 'react';
import { Maximize, Minimize } from 'lucide-react';
import Tooltip from '../tooltip';

interface MenuActionProps {
  onFullscreen?: (isFullscreen: boolean) => void;
  targetElementId?: string;
  targetElementSelector?: string;
  className?: string;
  autoDetectFullscreen?: boolean;
  forceCollapseState?: boolean;
  externalState?: boolean;
}

const MenuAction: React.FC<MenuActionProps> = ({
  onFullscreen,
  targetElementId,
  targetElementSelector,
  className = '',
  autoDetectFullscreen = true,
  forceCollapseState = false,
  externalState
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Check if fullscreen is supported
  const isFullscreenSupported = () => {
    return !!(
      document.fullscreenEnabled ||
      (document as any).webkitFullscreenEnabled ||
      (document as any).mozFullScreenEnabled ||
      (document as any).msFullscreenEnabled
    );
  };

  // Get the target element
  const getTargetElement = (): HTMLElement | null => {
    if (targetElementId) {
      return document.getElementById(targetElementId);
    }
    if (targetElementSelector) {
      return document.querySelector(targetElementSelector);
    }
    return document.querySelector('[data-chart-container]') || document.body;
  };

  // Enter fullscreen
  const enterFullscreen = async (element: HTMLElement) => {
    try {
      if (element.requestFullscreen) {
        await element.requestFullscreen();
      } else if ((element as any).webkitRequestFullscreen) {
        await (element as any).webkitRequestFullscreen();
      } else if ((element as any).mozRequestFullScreen) {
        await (element as any).mozRequestFullScreen();
      } else if ((element as any).msRequestFullscreen) {
        await (element as any).msRequestFullscreen();
      }
    } catch (error) {
      console.error('Failed to enter fullscreen:', error);
    }
  };

  // Exit fullscreen
  const exitFullscreen = async () => {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        await (document as any).webkitExitFullscreen();
      } else if ((document as any).mozCancelFullScreen) {
        await (document as any).mozCancelFullScreen();
      } else if ((document as any).msExitFullscreen) {
        await (document as any).msExitFullscreen();
      }
    } catch (error) {
      console.error('Failed to exit fullscreen:', error);
    }
  };

  // Handle fullscreen toggle
  const handleFullscreenToggle = async () => {
    if (!isFullscreenSupported() && !onFullscreen) {
      return;
    }

    setIsTransitioning(true);

    if (onFullscreen) {
      // Simply call the callback - let parent handle the logic
      onFullscreen(true);
    } else {
      // Use native fullscreen API
      if (isFullscreen) {
        await exitFullscreen();
      } else {
        const targetElement = getTargetElement();
        if (targetElement) {
          await enterFullscreen(targetElement);
        }
      }
    }

    setTimeout(() => setIsTransitioning(false), 300);
  };

  // Listen for fullscreen changes
  useEffect(() => {
    if (!autoDetectFullscreen) return;

    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      );
      setIsFullscreen(isCurrentlyFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, [autoDetectFullscreen]);

  const isSupported = isFullscreenSupported() || !!onFullscreen;
  const displayFullscreen = forceCollapseState || (externalState !== undefined ? externalState : isFullscreen);
  const tooltipMessage = displayFullscreen ? 'Collapse visual' : 'Expand visual';

  return (
    <Tooltip message={tooltipMessage}>
      <button
        type="button"
        onClick={handleFullscreenToggle}
        disabled={!isSupported || isTransitioning}
        className={`p-2 group flex items-center justify-center rounded-full transition-colors active:text-primary text-primary-text hover:bg-secondary-background hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      >
        {isTransitioning ? (
          <div className="w-5 h-5 animate-pulse">
            <Maximize className="w-full h-full" strokeWidth={1.75}/>
          </div>
        ) : displayFullscreen ? (
          <Minimize className="w-5 h-5" strokeWidth={1.75}/>
        ) : (
          <Maximize className="w-5 h-5" strokeWidth={1.75}/>
        )}
      </button>
    </Tooltip>
  );
};

export default MenuAction;